import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { fetchSupermarketCatalogProducts } from '../../services/shoppingListService';
import { ShoppingListItem, SHOPPING_LIST_MAX_ITEMS, SupermarketCatalogProduct } from '../../types/shoppingList';
import { createLogger } from '../../utils/logger';

const logger = createLogger('ShoppingListProductPicker');

interface ShoppingListProductPickerProps {
  supermarketId: string;
  existingItems: ShoppingListItem[];
  onAddProduct: (product: ShoppingListItem) => void;
  disabled?: boolean;
}

export default function ShoppingListProductPicker({
  supermarketId,
  existingItems,
  onAddProduct,
  disabled = false,
}: ShoppingListProductPickerProps) {
  const [catalog, setCatalog] = useState<SupermarketCatalogProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const existingProductIds = useMemo(
    () => new Set(existingItems.map((item) => item.productId)),
    [existingItems]
  );

  const atUniqueItemLimit = existingItems.length >= SHOPPING_LIST_MAX_ITEMS;

  const canAddProduct = useCallback(
    (productId: string) => !atUniqueItemLimit || existingProductIds.has(productId),
    [atUniqueItemLimit, existingProductIds]
  );

  const loadCatalog = useCallback(async () => {
    if (!supermarketId) {
      setCatalog([]);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const products = await fetchSupermarketCatalogProducts(supermarketId);
      setCatalog(products);
    } catch (error) {
      logger.error('Failed to load supermarket catalog', error);
      setLoadError('Could not load products for this supermarket. Please try again.');
      setCatalog([]);
    } finally {
      setLoading(false);
    }
  }, [supermarketId]);

  useEffect(() => {
    setSearchTerm('');
    loadCatalog();
  }, [loadCatalog]);

  const filteredCatalog = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return catalog;
    return catalog.filter(
      (product) =>
        product.name.toLowerCase().includes(normalized) ||
        product.category.toLowerCase().includes(normalized)
    );
  }, [catalog, searchTerm]);

  const handleAdd = (product: SupermarketCatalogProduct) => {
    if (disabled || !canAddProduct(product.productId)) return;
    onAddProduct({
      productId: product.productId,
      name: product.name,
      price: product.price,
      quantity: 1,
    });
  };

  if (!supermarketId) {
    return (
      <p className="text-muted small mb-0">
        Choose a supermarket above to browse products you can add to this list.
      </p>
    );
  }

  return (
    <div className="shopping-list-product-picker border rounded p-3 mb-3">
      <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 mb-2">
        <Form.Label className="mb-0 fw-semibold">Add from supermarket</Form.Label>
        {atUniqueItemLimit && (
          <small className="text-danger">
            Unique product limit reached ({SHOPPING_LIST_MAX_ITEMS}). You can still add +1 to
            products already on the list.
          </small>
        )}
      </div>
      <Form.Control
        type="search"
        placeholder="Search products by name or category..."
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        disabled={disabled || loading}
        className="mb-2"
        aria-label="Search supermarket products"
      />
      {loadError && <p className="text-danger small mb-2">{loadError}</p>}
      {loading ? (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" role="status" className="me-2" />
          Loading products...
        </div>
      ) : filteredCatalog.length === 0 ? (
        <p className="text-muted small mb-0">
          {catalog.length === 0
            ? 'No in-stock products found at this supermarket.'
            : 'No products match your search.'}
        </p>
      ) : (
        <ListGroup variant="flush" className="shopping-list-catalog-list">
          {filteredCatalog.map((product) => {
            const alreadyOnList = existingProductIds.has(product.productId);
            return (
              <ListGroup.Item
                key={product.productId}
                className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between px-0 gap-2"
              >
                <div className="flex-grow-1">
                  <div className="fw-semibold text-break">{product.name}</div>
                  <small className="text-muted">
                    ${product.price.toFixed(2)} · {product.category} · {product.availableQuantity}{' '}
                    in stock
                  </small>
                </div>
                <Button
                  variant={alreadyOnList ? 'outline-secondary' : 'outline-primary'}
                  size="sm"
                  onClick={() => handleAdd(product)}
                  disabled={disabled || !canAddProduct(product.productId)}
                  aria-label={
                    alreadyOnList
                      ? `Add another ${product.name} to list`
                      : `Add ${product.name} to list`
                  }
                >
                  {alreadyOnList ? 'Add +1' : 'Add'}
                </Button>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}
    </div>
  );
}
